import type { ThemeConfig } from 'antd';
import light from '@ui-library/tokens/light.json';

const { next } = light;

/**
 * Shared global token overrides for "next" variant components.
 * These apply to any Ant component inside the ConfigProvider.
 */
// ts-prune-ignore-next
export const nextGlobalTokens: ThemeConfig['token'] = {
  controlHeight: parseInt(next.controlHeight, 10),
  controlHeightLG: parseInt(next.controlHeightLG, 10),
  controlHeightSM: parseInt(next.controlHeightSM, 10),
  borderRadius: parseInt(next.borderRadius, 10),
  borderRadiusLG: parseInt(next.borderRadius, 10),
  borderRadiusSM: parseInt(next.borderRadius, 10),
  colorBorder: next.colorBorder,
  colorError: next.colorError,
  colorText: next.colorText,
  colorTextPlaceholder: next.colorTextPlaceholder,
  colorTextDisabled: next.palette.grey400,
  colorBgContainer: next.colorBgField,
  colorBgContainerDisabled: next.colorBgDisabled,
};

/**
 * Shared Input component tokens — used by both:
 * - Input
 * - InputNumber.
 *
 * Handles hover border, focus border + shadow, and error shadow via tokens.
 */
const nextInputComponentTokens = {
  hoverBorderColor: next.colorBorderHover,
  activeBorderColor: next.colorBorderFocus,
  activeShadow: next.boxShadowFocus,
  errorActiveShadow: next.boxShadowError,
};

/**
 * ConfigProvider theme for:
 * - Input
 * - TextArea
 * - Password
 * - Search
 * - InputNumber
 *
 * with variant="next".
 */
export const nextInputTheme: ThemeConfig = {
  token: nextGlobalTokens,
  components: {
    Input: {
      ...nextInputComponentTokens,
    },
    InputNumber: {
      ...nextInputComponentTokens,
    },
  },
};

/**
 * ConfigProvider theme for Select with variant="next".
 */
export const nextSelectTheme: ThemeConfig = {
  token: nextGlobalTokens,
  components: {
    Select: {
      selectorBg: next.colorBgField,
      optionSelectedBg: next.palette.grey100,
      optionActiveBg: next.palette.grey100,
      optionSelectedFontWeight: next.fontWeight400,
      optionFontSize: parseInt(next.fontSize200, 10),
      optionHeight: 32,
      optionPadding: '4px 8px',
      multipleItemBg: next.colorBgField,
      multipleItemBorderColor: next.palette.grey75,
      multipleItemHeight: 20,
    },
  },
};

/**
 * ConfigProvider theme for Menu with variant="next".
 */
export const nextMenuTheme: ThemeConfig = {
  token: nextGlobalTokens,
  components: {
    Menu: {
      itemBg: next.colorBgField,
      itemSelectedBg: next.palette.grey100,
      itemActiveBg: next.palette.grey100,
      itemSelectedColor: next.colorText,
      itemColor: next.palette.grey700,
      groupTitleColor: next.colorText,
      itemHoverBg: next.palette.grey100,
      itemHoverColor: next.colorText,
      itemHeight: 32,
      fontSize: parseInt(next.fontSize200, 10),
    },
  },
};

/**
 * ConfigProvider theme for:
 * - DatePicker
 * - RangePicker
 *
 * with variant="next".
 *
 * DatePicker has no input-trigger component tokens (hoverBorderColor, activeShadow, etc.)
 * so focus/hover/error shadows are handled via global tokens + minimal CSS.
 */
export const nextDatePickerTheme: ThemeConfig = {
  token: nextGlobalTokens,
};

/**
 * ConfigProvider theme for Button with variant="next".
 *
 * Sets shared sizing/radius. Per-level/intent colors are handled via
 * styled-components CSS since Ant's Button tokens don't support multiple
 * visual variants within a single theme.
 */
export const nextButtonTheme: ThemeConfig = {
  token: {
    ...nextGlobalTokens,
    colorPrimary: next.colorBorderFocus, // #105ED5
  },
  components: {
    Button: {
      borderRadius: parseInt(next.borderRadius, 10),
      borderRadiusLG: parseInt(next.borderRadius, 10),
      borderRadiusSM: parseInt(next.borderRadius, 10),
      controlHeight: parseInt(next.controlHeight, 10),
      controlHeightLG: parseInt(next.controlHeightLG, 10),
      controlHeightSM: parseInt(next.controlHeightSM, 10),
      contentFontSize: parseInt(next.fontSize200, 10),
      contentFontSizeSM: parseInt(next.fontSize100, 10),
      contentFontSizeLG: parseInt(next.fontSize200, 10),
      paddingInline: 8,
    },
  },
};
